import { KeyValue } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ApplicationQuery } from '@app/store/application/application.query';
import { ApplicationService } from '@app/store/application/application.service';
import { BrandConfigQuery } from '@app/store/brand/brand.query';
import { BreedsQuery } from '@app/store/breeds/breeds.query';
import { QuoteStore } from '@app/store/quote/quote.store';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { QuoteSessionService } from '@app/store/session/session.service';
import { formatPhoneNumber, stripSpecialChars } from '@app/utils/phone-number-formatter';
import { Ages } from '@constants/ages';
import { EVENTS, FORM_FIELD_TYPES, FORM_NAMES, REDACTED, CLICK_LOCATIONS, CLICK_TYPES, ERROR_TYPES } from '@constants/analytics';
import { getCdnAsset } from '@constants/api-endpoints';
import { API_ERRORS } from '@constants/api-errors';
import { filteredBreeds } from '@constants/breeds';
import { DATADOG_EVENTS, DATADOG_PAGES } from '@constants/datadog';
import { GENERIC_PARTNER_ORG_CODE } from '@constants/generic-partner';
import {
	AB_153_PAGE_ONE_ENHANCED_CONTENT,
	QE_PET_INFORMATION_SUBMIT_GOAL,
	VARIATION_ON,
} from '@constants/kameleoon';
import { NAME_MAX_LENGTH } from '@constants/name-length';
import { REFERRAL_LIST } from '@constants/referral-list';
import { ROUTES } from '@constants/routes';
import STATES from '@constants/state-list.json';
import { petNameRegEx } from '@constants/validation-regex';
import { YES_NO } from '@constants/YesNo';
import { datadogRum } from '@datadog/browser-rum';
import { Breed } from '@models/Breed';
import { Contact } from '@models/Contact';
import { QuoteSession } from '@models/QuoteSession';
import { AnalyticsService } from '@services/analytics.service';
import { KameleoonService } from '@services/kameleoon.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { PlacesService } from '@services/places.service';
import { RetrieveCookieService } from '@services/retrieve-cookie.service';
import { UtmService } from '@services/utm.service';
import { StatsigService } from '@statsig/angular-bindings';
import { isModernLink } from '@utils/brand.util';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { filter, map, takeUntil, pairwise } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import { AdobeTagService } from './../../services/adobeTag.service';

@Component({
	selector: 'qe-pet-information',
	templateUrl: './pet-information.component.html',
	styleUrls: ['./pet-information.component.scss']
})
export class PetInformationComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(`employerNameEl`)
	private readonly employerNameEl?: ElementRef<HTMLInputElement>;
	public showAgeError: boolean = false;
	public readonly brandPhoneNumber$: Observable<string | undefined>;
	public ageListMock: Map<number, string> = new Ages().values;
	public heardAboutMock: Array<KeyValue<number, string>> = REFERRAL_LIST;
	public catBreeds: Breed[] = [];
	public dogBreeds: Breed[] = [];
	public petInformationForm!: FormGroup;
	public employerForm!: FormGroup;
	public showAgentForm: boolean = false;
	public agentForm!: FormGroup;
	public formInvalid: boolean = false;
	public policyHolderExists: boolean = false;
	public ruleError: boolean = false;
	public ruleErrorList: string[] = [];
	public quoteRetrievalError: boolean = false;
	public focusedInput: string | undefined = undefined;
	public serviceDogEnabled: boolean = false;
	public showServiceDogOption: boolean = false;
	public showEnhancedContent: boolean = false;
	public pets: FormGroup[] = [];
	public readonly stateList: Array<{ name: string; abbreviation: string }> = STATES;
	/** Will hide/show employer form for Generic Org code entries */
	public showEmployerForm: boolean = false;
	public readonly afterYouEnroll: string[] = [
		`Embrace will request your pet's medical records from your Vet`,
		`After your waiting period, your pet is covered`,
		`Rest assured coverage will be there when you need it`
	];
	public submittingForm = false;
	public smsTermsAndConditionsUrl: string;
	public privacyPolicyUrl: string;
	public myEmbraceUrlChangeCoverage: string;
	public myEmbraceUrlAddAPet: string;
	public checkmarkBackgroundColor: string = 'brandPrimary';
	public checkmarkColor: string = 'white';
	public getCdnAsset = getCdnAsset;
	private readonly kameleoonEnabled$!: Observable<boolean | undefined>;
	private enableABTesting: boolean | undefined = true;
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
	private readonly zipLength: number = 5;
	private readonly pageTitle = 'Pet Information | Embrace QuoteEngine';
	private params!: ParamMap;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly adobeTagService: AdobeTagService,
		private readonly router: Router,
		private readonly session: QuoteSessionQuery,
		private readonly fb: FormBuilder,
		private readonly titleService: Title,
		private readonly storeActions: QuoteSessionService,
		private readonly appActions: ApplicationService,
		private readonly appQuery: ApplicationQuery,
		private readonly sessionQuery: QuoteSessionQuery,
		private readonly breedsQuery: BreedsQuery,
		private readonly orchestratorService: OrchestratorService,
		private readonly gtmService: AnalyticsService,
		private readonly utmService: UtmService,
		private readonly dialog: MatDialog,
		private readonly recaptchaV3Service: ReCaptchaV3Service,
		private readonly brandConfigQuery: BrandConfigQuery,
		private readonly placesService: PlacesService,
		private readonly kameleoonService: KameleoonService,
		private readonly quoteStore: QuoteStore,
		private readonly retrieveCookieService: RetrieveCookieService,
		private readonly statsigService: StatsigService
	) {
		this.smsTermsAndConditionsUrl = environment.smsTermsAndConditionsUrl;
		this.privacyPolicyUrl = environment.privacyPolicyUrl;
		this.myEmbraceUrlChangeCoverage = `${environment.myEmbraceUrl}#/ChangeCoverage`;
		this.myEmbraceUrlAddAPet = `${environment.myEmbraceUrl}#/ChangeCoverage?referral=addpet`;
		this.brandPhoneNumber$ = this.brandConfigQuery.phone$().pipe(map(phone => formatPhoneNumber(phone)));
		this.quoteRetrievalError = this.router.getCurrentNavigation()?.extras.state?.quoteRetrievalError;
		this.kameleoonEnabled$ = this.appQuery.getKameleoonEnabled$();

		this.kameleoonEnabled$.subscribe((enable) => {
			this.enableABTesting = enable;
		});

		this.route.queryParamMap.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(params => {
			if (params.keys.length) {
				this.params = params;
			}

			if (params.keys.includes('petName')) {
				this.focusedInput = 'petName0';
			}

			if (params.keys.includes('zip') || params.keys.includes('zipCode') || params.keys.includes('zip_code')) {
				this.focusedInput = 'zipCode';
			}
		});

		this.breedsQuery.selectAll().pipe(takeUntil(this.ngUnsubscribe$)).subscribe(allBreeds => {
			this.dogBreeds = allBreeds.filter(({ speciesName, id }) =>
				// eslint-disable-next-line @typescript-eslint/no-magic-numbers
				speciesName.toLowerCase() === 'dog' && !filteredBreeds.includes(+id)
			);

			this.catBreeds = allBreeds.filter(({ speciesName, id }) =>
				// eslint-disable-next-line @typescript-eslint/no-magic-numbers
				speciesName.toLowerCase() === 'cat' && !filteredBreeds.includes(+id)
			);

			this.getPetFormGroup(0)?.patchValue({ breedList: this.dogBreeds });
		});

		this.petInformationForm = this.fb.group(
			{
				pets: this.fb.array([this.createPetForm(this.params?.get(`petName`))], Validators.required),
				zipCode: [
					this.zipCodeParamValue,
					{
						validators: [
							Validators.required,
							Validators.minLength(this.zipLength),
							Validators.maxLength(this.zipLength),
							Validators.pattern(/^\d*$/im)
						],
						updateOn: `blur`
					}
				],
				emailAddress: ['', [Validators.required, Validators.email]],
				phoneNumber: [
					'',
					[
						/* eslint-disable @typescript-eslint/no-magic-numbers */
						Validators.minLength(10),
						Validators.maxLength(16)
					]
				],
				optInTextMessage: [false],
				heardAbout: [null],
				military: ['no', Validators.required],
			},
			{ updateOn: `blur` }
		);

		this.petInformationForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(ev => {
			if (this.formInvalid) {
				this.formInvalid = false;
				this.petInformationForm.updateValueAndValidity();
			}
		});

		this.petsArrayControl.valueChanges
			.pipe(
				pairwise(),
				filter(([oldItemsArray, newItemsArray]) => {
					for (let i = 0; i < oldItemsArray.length && oldItemsArray.length === newItemsArray.length; i++) {
						if (newItemsArray[i].species !== oldItemsArray[i].species) {
							this.petsArrayControl.at(i).patchValue({ breed: '' });
						}
					}

					return true;
				})).subscribe();

		this.brandConfigQuery
			.featureFlags$()
			.pipe(
				takeUntil(this.ngUnsubscribe$)
			).subscribe(flags => {
				this.serviceDogEnabled = flags?.find((flag) => flag?.feature === 'service_dog')?.enabled || false;
			});

		this.brandConfigQuery
			.showAgentForm$()
			.pipe(
				takeUntil(this.ngUnsubscribe$)
			).subscribe(showAgentForm => {
				this.showAgentForm = showAgentForm;
				if (this.showAgentForm) {
					this.initAgentForm();
				}
			});

		this.sessionQuery
			.contactState$()
			.pipe(
				takeUntil(this.ngUnsubscribe$),
				filter(contact => !!contact)
			)
			.subscribe(contact => {
				if (contact.emailAddress) {
					this.emailAddress?.patchValue(contact.emailAddress);
				}
				if (contact.phoneNumber) {
					this.phoneNumber?.patchValue(contact.phoneNumber);
				}
				if (contact.ratingZipCode) {
					this.zipCode?.patchValue(contact.ratingZipCode);
				}
			});
	}

	public get zipCode(): AbstractControl | null {
		return this.petInformationForm.get(`zipCode`);
	}
	public get emailAddress(): AbstractControl | null {
		return this.petInformationForm.get(`emailAddress`);
	}
	public get phoneNumber(): AbstractControl | null {
		return this.petInformationForm.get(`phoneNumber`);
	}
	public get optInTextMessage(): AbstractControl | null {
		return this.petInformationForm.get(`optInTextMessage`);
	}
	public get heardAbout(): AbstractControl | null {
		return this.petInformationForm.get(`heardAbout`);
	}
	public get military(): AbstractControl | null {
		return this.petInformationForm.get(`military`);
	}
	public get militaryService(): boolean | null {
		const value = this.military?.value;

		return YES_NO.get(value) ?? value;
	}
	public get policyHasPets(): boolean {
		return this.sessionQuery.getValue().pets?.length > 0;
	}
	public get partnerSenderId(): AbstractControl | null {
		return this.agentForm.get(`partnerSenderId`);
	}
	// Employer Info
	public get employerName(): AbstractControl | null {
		return this.employerForm.get(`name`);
	}
	public get employerAddress(): AbstractControl | null {
		return this.employerForm.get(`address`);
	}
	public get employerCity(): AbstractControl | null {
		return this.employerForm.get(`city`);
	}
	public get employerState(): AbstractControl | null {
		return this.employerForm.get(`state`);
	}
	public get employerZipCode(): AbstractControl | null {
		return this.employerForm.get(`zipCode`);
	}
	public get showAllstateOptOut(): boolean {
		return this.brandParamValue.toLowerCase() === 'allstate';
	}
	public get formattedBrand(): string {
		return this.brandParamValue.slice(0, 1).toUpperCase() + this.brandParamValue.slice(1);
	}
	public get petsArrayControl(): FormArray {
		return <FormArray>this.petInformationForm?.get('pets') || [];
	}
	public get submitButtonLabel(): string {
		return this.showEnhancedContent ? 'Check Pricing' : 'Select Coverage';
	}
	private get zipCodeParamValue(): string {
		return (this.params?.get('zipCode') || this.params?.get('zip') || this.params?.get('zip_code')) ?? '';
	}
	private get brandParamValue(): string {
		return this.params?.get('brand') || '';
	}

	async ngOnInit(): Promise<void> {
		this.titleService.setTitle(this.pageTitle);
		const orgCode = await firstValueFrom(this.sessionQuery.organizationCode$());
		const brand = await firstValueFrom(this.sessionQuery.brand$());
		if (brand.toLowerCase() === 'allstate') {
			this.adobeTagService.loadAllstateScript(environment.allstateTagManagerURL);
		}

		if (this.titleService.getTitle() === typeof String) {
			throw new Error('This is a test error');
		}

		this.showEmployerForm = orgCode === GENERIC_PARTNER_ORG_CODE;

		if (this.showEmployerForm) this.initEmployerForm();

		// This event triggers important lead tracking tags in GTM like Impact. Don't touch.
		this.gtmService.push({
			event: 'checkout',
			pagePath: 'pet-information',
			// eslint-disable-next-line max-len
			originalLocation: `${document.location.protocol}//${document.location.hostname}${document.location.pathname}${document.location.search}`
		});

		// Google Analytics 4
		this.gtmService.push({
			event: 'quote_engine_checkout_ga4',
			checkoutStep: 1,
			funnelStepName: 'Pet Information',
			conversionName: 'Quote Start',
			brandName: brand.toLowerCase()
		});

		await this.kameleoonService.initializeClient(brand.toLowerCase());

		const analyticsInfo = this.utmService.getUtmCookie();

		const user = {
			userID: undefined,
			brand: brand.toLowerCase(),
			utm_source: analyticsInfo.source,
			utm_campaign: analyticsInfo.campaign,
			utm_medium: analyticsInfo.medium,
			utm_term: analyticsInfo.term,
			utm_content: analyticsInfo.content,
		};

		this.statsigService.updateUserAsync(user)
			.then(() => {
				const experiment = this.statsigService.getExperiment('poc_experiment');
				const experimentValue = experiment.get('experimentEvaluated', false);
				console.log('Experiment evaluation 1: ', experimentValue);
				const experiment2 = this.statsigService.getExperiment('poc_experiment_-_stableid');
				const experimentValue2 = experiment2.get('experimentEvaluated', false);
				console.log('Experiment evaluation 2: ', experimentValue2);
			})
			.catch((error) => {
				console.error('Error updating user:', error);
			});

		if (this.enableABTesting) {
			this.kameleoonService.addPageView(window.location.href, this.pageTitle);

			this.showEnhancedContent = this.kameleoonService.getActiveFeatureFlagDetails(AB_153_PAGE_ONE_ENHANCED_CONTENT) === VARIATION_ON;

			const brandName = await firstValueFrom(this.session.brand$());

			if (brandName.toLowerCase() === 'embrace') {
				this.checkmarkBackgroundColor = 'checkMark-Background';
				this.checkmarkColor = 'white';
			}
		}

		datadogRum.addAction(DATADOG_EVENTS.PAGE_VIEW, {
			page: DATADOG_PAGES.PET_INFO,
		});
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			if (this.employerNameEl) {
				this.placesService
					.initAutocomplete(this.employerNameEl.nativeElement, {
						fields: [`address_components`, `name`],
						types: [`establishment`]
					})
					.pipe(takeUntil(this.ngUnsubscribe$))
					.subscribe(({ businessName, address }) => {
						this.employerForm.patchValue({
							name: businessName,
							address: address.addressLine1,
							city: address.city,
							state: address.state,
							zipCode: address.zipCode
						});
					});
			}
			this.gtmService.push({
				event: 'Loaded Pet Information Page (New Quote)'
			});
		});
	}

	public checkPhoneNumber(): void {
		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: 'phoneNumber',
			field_value: REDACTED,
			field_type: FORM_FIELD_TYPES.INPUT,
			field_id: `${FORM_FIELD_TYPES.INPUT}-${FORM_NAMES.PET_INFO}-phoneNumber`
		});
		this.focusedInput = undefined;
	}

	public handleOptInTextMessageCheckbox(el: HTMLEpiCheckboxElement): void {
		this.optInTextMessage?.setValue(el.checked);
		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: 'optInTextMessage',
			field_value: el.value,
			field_type: FORM_FIELD_TYPES.CHECKBOX,
			field_id: `${FORM_FIELD_TYPES.CHECKBOX}-${FORM_NAMES.PET_INFO}-optInTextMessage`
		});
	}

	public petNameFocused(index: number): boolean {
		return this.focusedInput === `petName${index}`;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public getPetFormGroup(index: number): any {
		return this.petsArrayControl.controls && this.petsArrayControl.controls[index];
	}

	public handleEpiSelectionChange(el: HTMLEpiSelectElement): void { }

	// eslint-disable-next-line complexity
	public async submitForm(): Promise<void> {
		this.submittingForm = true;
		const recaptchaToken = await firstValueFrom(this.recaptchaV3Service.execute('start_quote'));
		this.quoteRetrievalError && (this.quoteRetrievalError = false);
		this.policyHolderExists && (this.policyHolderExists = false);
		this.ruleError && (this.ruleError = false);
		this.ruleErrorList.length = 0;
		this.petInformationForm.markAllAsTouched();
		if (
			this.petInformationForm.invalid ||
			(this.showEmployerForm && this.employerForm.invalid) ||
			(this.showAgentForm && this.agentForm.invalid)
		) {
			this.formInvalid = true;
			this.submittingForm = false;

			return;
		}

		const contact = this.sessionQuery.getContact();
		const {
			zipCode: ratingZipCode,
			emailAddress,
			phoneNumber,
			heardAbout,
			pets
		} = this.petInformationForm.getRawValue();

		interface formPet {
			age: string | number;
			breed: string;
			species: string;
			sex: string;
			name: string;
			serviceDog: string;
		}

		// look for any pets under 6weeks and return if present
		const hasPetUnder6Weeks = pets.some((p: formPet) => p.age === '999' ? true : false);

		if (hasPetUnder6Weeks) {
			this.showAgeError = true;
			this.submittingForm = false;

			return;
		}

		// transform pets for request
		const transformedPets = pets.map((p: formPet) => ({
			name: p.name,
			species: p.species,
			breedId: p.breed,
			gender: p.sex,
			breedName: this.getBreedNameById(p.breed),
			isServiceDog: p.serviceDog === 'yes' ? true : false,
			ageInYears: p.age
		}));

		// show loader
		this.appActions.setLoading(true);

		const strippedPhoneNumber = stripSpecialChars(phoneNumber);
		const cleanedPhoneNumber = strippedPhoneNumber.startsWith('1') ? strippedPhoneNumber.slice(1) : strippedPhoneNumber;
		const analyticsInfo = this.utmService.getUtmCookie();
		const session = await firstValueFrom(this.sessionQuery.session$());

		// if phone number is valid, the opt in value should be true, else false
		const optInTextMessageAndValidPhoneNumber = cleanedPhoneNumber.length === 10;

		let generateQuoteRequest: Partial<QuoteSession> = {
			contact: {
				ratingZipCode,
				emailAddress,
				phoneNumber: cleanedPhoneNumber,
				organizationCode: contact.organizationCode,
				organizationMemberIdentifier: contact.organizationMemberIdentifier,
				optInTextMessage: optInTextMessageAndValidPhoneNumber,
				heardAboutUs: heardAbout ?? 0,
				firstName: contact?.firstName ?? ``,
				lastName: contact?.lastName ?? ``,
				partnerSenderId: contact.partnerSenderId ?? ``,
				brand: contact.brand ?? ``,
				mailingAddress: contact.mailingAddress,
				billingInformation: contact.billingInformation,
				military: !!this.militaryService
			},
			pets: [
				...transformedPets
			],
			analyticsInfo,
			delayPolicyStartDateDays: session.delayPolicyStartDateDays,
			subProducerNumber: session.subProducerNumber,
			verificationInfo: {
				expectedAction: 'start_quote',
				captchaToken: recaptchaToken
			},
		};
		this.quoteStore.update(generateQuoteRequest);

		/** If the user gets the employer form it should send that info in the contact */
		if (this.showEmployerForm) {
			const { name: employerName, address: addressLine1, city, state, zipCode } = this.employerForm.getRawValue();
			const employerInfo: Pick<Contact, 'employerAddress' | 'employerName'> = {
				employerAddress: {
					addressLine1,
					city,
					state,
					zipCode
				},
				employerName
			};
			generateQuoteRequest = {
				...generateQuoteRequest,
				contact: {
					...generateQuoteRequest.contact,
					...employerInfo
				}
			};
		}

		if (this.showAgentForm) {
			const { partnerSenderId } = this.agentForm.getRawValue();

			generateQuoteRequest = {
				...generateQuoteRequest,
				contact: {
					...generateQuoteRequest.contact,
					partnerSenderId
				}
			};
		}

		if (this.enableABTesting) {
			this.kameleoonService.trackConversion(QE_PET_INFORMATION_SUBMIT_GOAL);
		}

		this.statsigService.logEvent('page_1_submit', 1, {
			ratingZipCode,
			brand: contact.brand ?? 'embrace',
			numberOfPets: transformedPets.length
		});

		this.gtmService.push({
			event: EVENTS.CLICK,
			click_location: CLICK_LOCATIONS.CONTENT_BOTTOM,
			click_text: 'Select Coverage',
			click_type: CLICK_TYPES.CTA,
			click_id: `${CLICK_TYPES.CTA}-${CLICK_LOCATIONS.CONTENT_BOTTOM}-Select-Coverage`
		});

		this.orchestratorService
			.generateNewQuote(generateQuoteRequest)
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(
				quoteSession => {
					if (quoteSession) {
						this.storeActions.update({
							...quoteSession,
							quoteDetails: {
								...quoteSession.quoteDetails
							},
							contact: {
								...quoteSession.contact,
								vetSearchZip: ratingZipCode
							}
						});

						this.gtmService.push({
							event: 'Clicked Start Quote'
						});

						this.gtmService.push({
							event: 'start_a_quote_ga4',
							initialPetSpecies: pets[0].species,
							initialPetSex: pets[0].gender,
							initialPetBreed: this.getBreedNameById(pets[0].breedId).toLowerCase(),
							initialPetAge: pets[0].ageInYears
						});

						if (quoteSession.contact.id) {
							this.retrieveCookieService.update(quoteSession.contact.id);
						}

						datadogRum.addAction(DATADOG_EVENTS.PAGE_SUBMIT, {
							page: DATADOG_PAGES.PET_INFO,
							numberOfPets: pets.length,
							quoteId: quoteSession.id
						});

						// dirty
						this.appActions.setActivePet(quoteSession.pets[0].petId ?? ``);
						this.router.navigate([ROUTES.SELECT_A_POLICY]);
					}
				},
				(err: HttpErrorResponse) => {
					const { detail, title } = err.error;
					this.appActions.setLoading(false);
					this.submittingForm = false;
					switch (title) {
						case API_ERRORS.QUOTE_EXECUTION_ERROR:
						case API_ERRORS.CONTACT_VALIDATION_MESSAGE:
						case API_ERRORS.PAYROLLDEDUCT_VALIDATION_MESSAGE:
							this.ruleError = true;
							this.ruleErrorList = [
								...this.ruleErrorList,
								...err.error.detail
									?.split(` :: `)[1]
									?.split(`;`)
									?.map((message: string) => message.trim())
							];

							return;
						case API_ERRORS.POLICY_HOLDER_EXISTS:
							this.policyHolderExists = true;

							return;
						default:
							break;
					}

					if (title === API_ERRORS.CA_SALES_VALIDATION_MESSAGE || title === API_ERRORS.ME_SALES_VALIDATION_MESSAGE) {
						const figoState = title === API_ERRORS.ME_SALES_VALIDATION_MESSAGE
							? 'ME' : (title === API_ERRORS.CA_SALES_VALIDATION_MESSAGE ? 'CA' : undefined);

						this.storeActions.update({
							figoState
						});

						this.gtmService.push({
							event: EVENTS.ERROR,
							error_type: ERROR_TYPES.API,
							error_text: `Figo Link Out ${figoState}`,
							error_id: `error_${ERROR_TYPES.API}-${title}`
						});

						this.router.navigate([ROUTES.PREQUOTE], {
							state: {
								detail: detail
									?.split(` :: `)[1]
									?.split(`;`)
									?.map((message: string) => message.trim())
							}
						});
					}
					else {
						const errorDetail = detail
							?.split(` :: `)[1]
							?.split(`;`)
							?.map((message: string) => message.trim());

						this.gtmService.push({
							event: EVENTS.ERROR,
							error_type: ERROR_TYPES.GENERIC,
							error_text: errorDetail,
							error_id: `error_${ERROR_TYPES.GENERIC}-${title}`
						});

						this.router.navigate([ROUTES.ERROR], {
							state: {
								detail: errorDetail
							}
						});
					}
					console.error(err);
				}
			);
	}

	public onFocus(el: string): void {
		this.focusedInput = el;
	}

	public onBlur(el: string): void {
		this.focusedInput = undefined;

		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: el,
			field_value: el === 'petName' ? this.petsArrayControl?.value[0].name : REDACTED,
			field_type: FORM_FIELD_TYPES.INPUT,
			field_id: `${FORM_FIELD_TYPES.INPUT}-${FORM_NAMES.PET_INFO}-${el}`
		});
	}

	public onRadioChange(formField: string, value: string, formIndex: number = 0): void {
		if (formField === 'pet_species') {
			// update breed list when species changes for each pet form group
			this.getPetFormGroup(formIndex).get('breedList').patchValue(value === 'dog' ? this.dogBreeds : this.catBreeds);

			this.showServiceDogOption = this.serviceDogEnabled && value === 'dog';
		}
		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: formField,
			field_value: value,
			field_type: FORM_FIELD_TYPES.RADIO,
			field_id: `${FORM_FIELD_TYPES.RADIO}-${FORM_NAMES.PET_INFO}-${formField}`
		});
	}

	public openPrivacyPolicyModal(ev: UIEvent): void {
		ev.preventDefault();
		import(`@shared/privacy-policy-modal/privacy-policy-modal.component`)
			.then(c => this.dialog.open(c.PrivacyPolicyModalComponent))
			.catch(err => console.error(err));
	}

	public handleAddPet(): void {
		this.gtmService.push({
			event: EVENTS.CLICK,
			click_location: CLICK_LOCATIONS.CONTENT_TOP,
			click_text: 'Add Another Pet',
			click_type: CLICK_TYPES.BUTTON,
			click_id: `${CLICK_TYPES.BUTTON}-${CLICK_LOCATIONS.CONTENT_TOP}-Add-Another-Pet`
		});

		const petToAdd = this.createPetForm();
		this.petsArrayControl.push(petToAdd);

	}

	public handleRemovePet(index: number): void {
		this.gtmService.push({
			event: EVENTS.CLICK,
			click_location: CLICK_LOCATIONS.CONTENT_TOP,
			click_text: 'Remove Pet',
			click_type: CLICK_TYPES.BUTTON,
			click_id: `${CLICK_TYPES.BUTTON}-${CLICK_LOCATIONS.CONTENT_TOP}-Remove-Pet`
		});

		// if only one pet, don't remove it
		if (this.petsArrayControl.length !== 1) {
			this.petsArrayControl.removeAt(index);
		}
	}

	// TODO: Combine this with utility method
	public autoPopulateForm(): void {
		this.petInformationForm.patchValue({
			petName: 'TestName',
			sex: 'male',
			species: 'dog',
			zipCode: 12180,
			emailAddress: `sdiyudsy83478473yhfs@yopmail.com`,
			autoPopulate: true
		});
	}

	public autoPopulateMenus(): void {
		this.petInformationForm.patchValue({ age: '999' });
	}

	public isModernLinkBrand(): boolean {
		return isModernLink(this.brandParamValue);
	}

	private getBreedNameById(breedId: string): string {
		return this.breedsQuery.getAll().find(({ id }) => id === breedId)?.name ?? ``;
	}

	private initEmployerForm(): void {
		this.employerForm = this.fb.group(
			{
				name: ['', Validators.required],
				address: ['', Validators.required],
				city: ['', Validators.required],
				state: ['', Validators.required],
				zipCode: ['', Validators.required]
			},
			{ updateOn: `blur` }
		);
	}

	private initAgentForm(): void {
		this.agentForm = this.fb.group({
			partnerSenderId: ['', Validators.required]
		});
	}

	private createPetForm(petName: string | null = ''): FormGroup {
		return this.fb.group({
			name: [
				petName ? petName : '',
				[Validators.required, Validators.pattern(petNameRegEx), Validators.maxLength(NAME_MAX_LENGTH)]
			],
			species: ['dog', { validators: Validators.required, updateOn: `change` }],
			serviceDog: ['no', { updateOn: `change` }],
			sex: ['male', { validators: Validators.required, updateOn: `change` }],
			breed: ['', { validators: Validators.required, updateOn: `blur` }],
			age: ['', { validators: Validators.required, updateOn: `change` }],
			breedList: [this.dogBreeds, {}]
		});
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}
}
